import React, { useState, useEffect, useRef } from 'react';
import './Test.css';
import { useAuth } from '../context/AuthContext';
import { useAppTranslation } from "./TranslationContext";
import { useParams, Link } from "react-router-dom";
const API_BASE = 'https://api.eduskill.uz';

export const Test = () => {
  const { user, userKursData, setUserKursData } = useAuth();
  const { kursId } = useParams();
  const { t } = useAppTranslation();
  const lang = (localStorage.getItem('language') || 'uz').toLowerCase(); // 'uz' | 'ru' | 'en'

  // Состояния
  const [questions, setQuestions] = useState([]);
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState(null);
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(false);
  const [totalTime, setTotalTime] = useState(180);
  const [loading, setLoading] = useState(true);
  const [guardLoading, setGuardLoading] = useState(true);
  const [error, setError] = useState(null);

  // Данные финального теста (последняя тема)
  const [lastThemeId, setLastThemeId] = useState(null);
  const [lastThemeTartib, setLastThemeTartib] = useState(0);
  const [minScore, setMinScore] = useState(0);
  const [testCount, setTestCount] = useState(0);

  // user-kurs прогресс/идентификатор
  const [kursProgress, setKursProgress] = useState(0); // CHANGED: теперь хранит uk.tartib
  const [userKurs, setUserKurs] = useState(null);       // весь объект user-kurs (id/urunish/...)
  const answersRef = useRef([]);

  const authHeader = () => {
    const token = localStorage.getItem('token');
    return token ? { 'Authorization': `Token ${token}` } : {};
  };

  // 0) Защита маршрута: грузим user-kurs и темы, определяем «последнюю тему»
  useEffect(() => {
    let cancelled = false;

    const loadGuardsAndMeta = async () => {
      try {
        setGuardLoading(true);

        // 0.1 user-kurs
        let uk = userKursData;
        if (!uk) {
          const token = localStorage.getItem('token');
          if (!token || !user?.id) {
            setError('Auth required');
            setGuardLoading(false);
            return;
          }
          const res = await fetch(`${API_BASE}/api/user-kurs/?user=${user.id}&kurs=${kursId}`, {
            headers: { ...authHeader() }
          });
          if (!res.ok) throw new Error('Failed to load user-kurs');
          const data = await res.json();
          uk = data?.[0] ?? null;
        }

        // CHANGED: вместо uk.mavzu используем uk.tartib
        const currentProgress = Number(uk?.tartib ?? 0);

        if (!cancelled) {
          setKursProgress(currentProgress);
          setUserKurs(uk || null);
        }

        // 0.2 все темы курса -> находим последнюю (max tartib)
        const themesRes = await fetch(`${API_BASE}/api/kurs-mavzulari/?kurs=${kursId}`);
        if (!themesRes.ok) throw new Error('Failed to load themes');
        const themesData = await themesRes.json();
        if (!Array.isArray(themesData) || themesData.length === 0) {
          throw new Error('No themes found');
        }
        const sorted = themesData
          .map(i => ({
            id: i.id,
            tartib: Number(i.tartib),
            test_soni: Number(i.test_soni ?? 0),
            test_min_soni: Number(i.test_min_soni ?? 0),
          }))
          .sort((a, b) => a.tartib - b.tartib);

        const last = sorted[sorted.length - 1];
        if (!last) throw new Error('No last theme');

        if (!cancelled) {
          setLastThemeId(last.id);
          setLastThemeTartib(last.tartib);
          setTestCount(last.test_soni || 0);
          setMinScore(last.test_min_soni || 0);
        }

        // 0.3 guard: нельзя открывать финальный тест, пока не достигли последней темы
        // CHANGED: сравниваем с uk.tartib
        if (currentProgress < last.tartib) {
          if (!cancelled) {
            setError('Final test is locked until you finish all topics.');
            setGuardLoading(false);
          }
          return;
        }

        if (!cancelled) setGuardLoading(false);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Guard error');
          setGuardLoading(false);
        }
      }
    };

    loadGuardsAndMeta();
    return () => { cancelled = true; };
  }, [kursId, user, userKursData]);

  // 1) Загрузка вопросов для последней темы
  useEffect(() => {
    let cancelled = false;

    const loadQuestions = async () => {
      if (!lastThemeId) return;
      try {
        setLoading(true);

        const testsRes = await fetch(`${API_BASE}/api/kurs-mavzu-test/?mavzu=${lastThemeId}`);
        if (!testsRes.ok) throw new Error('Failed to load tests');
        const testsData = await testsRes.json();

        if (!Array.isArray(testsData) || testsData.length === 0) {
          if (!cancelled) {
            setQuestions([]);
            setTotalTime(0);
            setLoading(false);
          }
          return;
        }

        const shuffled = [...testsData].sort(() => 0.5 - Math.random());
        const selectedTests = shuffled.slice(0, Math.max(0, testCount));

        const makeAnswers = (item) => ({
          uz: [
            { text: item.V1_uz, isCorrect: true },
            { text: item.V2_uz, isCorrect: false },
            { text: item.V3_uz, isCorrect: false },
            { text: item.V4_uz, isCorrect: false },
          ],
          ru: [
            { text: item.V1_ru, isCorrect: true },
            { text: item.V2_ru, isCorrect: false },
            { text: item.V3_ru, isCorrect: false },
            { text: item.V4_ru, isCorrect: false },
          ],
          en: [
            { text: item.V1_en, isCorrect: true },
            { text: item.V2_en, isCorrect: false },
            { text: item.V3_en, isCorrect: false },
            { text: item.V4_en, isCorrect: false },
          ],
        });

        const prepared = selectedTests.map(item => {
          const qKey = `test_savol_${lang}`;
          const qText = item[qKey] ?? item.test_savol_uz ?? '';
          const answers = (makeAnswers(item)[lang] || makeAnswers(item).uz).sort(() => 0.5 - Math.random());
          return {
            question: qText,
            answers,
            testId: item.id,
          };
        });

        if (!cancelled) {
          setQuestions(prepared);
          setTotalTime(Math.max(0, (testCount || prepared.length) * 60)); // 1 мин/вопрос
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
          setLoading(false);
        }
      }
    };

    loadQuestions();
    return () => { cancelled = true; };
  }, [lastThemeId, testCount, lang]);

  // 2) Таймер
  useEffect(() => {
    if (finished) return;
    const timer = setInterval(() => {
      setTotalTime(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          handleFinish(true); // timeOver
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [finished]); // eslint-disable-line

  // 3) Переход к следующему вопросу
  const handleNext = () => {
    const currentQuestionObj = questions[current];
    const selectedAnswer = currentQuestionObj.answers[selected];
    const isCorrect = !!selectedAnswer?.isCorrect;

    if (isCorrect) {
      setScore(prev => prev + 1);
    }

    answersRef.current.push({
      user: user?.id,
      test: currentQuestionObj.testId,
      V: selectedAnswer?.text ?? null,
      javob_xolati: isCorrect,
    });

    setSelected(null);

    if (current + 1 < questions.length) {
      setCurrent(prev => prev + 1);
    } else {
      handleFinish(false);
    }
  };

  // 4) Завершение: отправка ответов, PATCH urunish/foiz, продвижение прогресса
  const handleFinish = async (timeOver) => {
    setFinished(true);

    // 4.0 финальный балл (если время вышло и был выбран ответ — учитываем его)
    const extra = timeOver && selected !== null && questions[current]?.answers[selected]?.isCorrect ? 1 : 0;
    const finalScore = score + extra;
    const percent = questions.length ? Math.round((finalScore / questions.length) * 100) : 0;
    const passed = finalScore >= minScore;

    // 4.1 отправляем каждый ответ (best-effort)
    for (const answer of answersRef.current) {
      try {
        const response = await fetch(`${API_BASE}/api/kurs-mavzu-test-natija/`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeader() },
          body: JSON.stringify(answer),
        });
        if (!response.ok) throw new Error(`Send answer failed: ${response.status}`);
      } catch (err) {
        console.error(err.message);
      }
    }

    // 4.2 PATCH user-kurs: urunish (+1) и urunishfoiz (процент)
    try {
      // получим свежие данные, если нет в state
      let uk = userKurs;
      if (!uk) {
        const res = await fetch(`${API_BASE}/api/user-kurs/?user=${user?.id}&kurs=${kursId}`, {
          headers: { ...authHeader() }
        });
        const data = await res.json();
        uk = data?.[0];
      }
      if (!uk) throw new Error("No user-kurs data for attempts/percent");

      const newUrunish = (Number(uk.urunish) || 0) + 1;

      const patchRes = await fetch(`${API_BASE}/api/user-kurs/${uk.id}/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({
          urunish: newUrunish,
          urunishfoiz: percent
        }),
      });
      if (!patchRes.ok) throw new Error("Failed to save attempt/percent");

      const updatedUk = { ...uk, urunish: newUrunish, urunishfoiz: percent };
      setUserKurs(updatedUk);
      setUserKursData(updatedUk);
    } catch (err) {
      console.error(err.message);
    }

    // 4.3 продвижение прогресса, если прошёл финал
    if (passed) {
      try {
        let uk = userKurs;
        if (!uk) {
          const res = await fetch(`${API_BASE}/api/user-kurs/?user=${user?.id}&kurs=${kursId}`, {
            headers: { ...authHeader() }
          });
          const data = await res.json();
          uk = data?.[0];
        }
        if (!uk) throw new Error("No user-kurs data for progress");

        // CHANGED: вместо uk.mavzu сверяем uk.tartib
        if (parseInt(uk.tartib) === parseInt(lastThemeTartib)) {
          const patchRes = await fetch(`${API_BASE}/api/user-kurs/${uk.id}/`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json", ...authHeader() },
            body: JSON.stringify({
              user: user?.id,
              kurs: parseInt(kursId),
              // CHANGED: повышаем tartib, и (опционально) сбрасываем elementtartib на 1
              tartib: parseInt(lastThemeTartib) + 1,
              elementtartib: 1, // CHANGED: чтобы начать следующую тему с первого элемента
            }),
          });
          if (!patchRes.ok) throw new Error("Progress save failed");

          // CHANGED: обновляем локально tartib/elementtartib
          const updatedUk = { ...(userKurs || {}), id: uk.id, tartib: parseInt(lastThemeTartib) + 1, elementtartib: 1 };
          setUserKurs(updatedUk);
          setUserKursData(updatedUk);
          setKursProgress(parseInt(lastThemeTartib) + 1);
        }
      } catch (err) {
        console.error(err.message);
      }
    }
  };

  // 5) Получить сертификат: ставим sertifikatga_ruxsat=true и открываем ссылку
  const handleGetCertificate = async () => {
    try {
      // актуальный uk/id
      let uk = userKurs;
      if (!uk) {
        const res = await fetch(`${API_BASE}/api/user-kurs/?user=${user?.id}&kurs=${kursId}`, {
          headers: { ...authHeader() }
        });
        const data = await res.json();
        uk = data?.[0];
        setUserKurs(uk || null);
      }
      const userKursId = uk?.id;
      if (!userKursId) throw new Error("No user-kurs id");

      // 1) Разрешаем сертификат
      const patchRes = await fetch(`${API_BASE}/api/user-kurs/${userKursId}/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({ sertifikatga_ruxsat: true })
      });
      if (!patchRes.ok) throw new Error("Failed to allow certificate");

      // 2) Получаем ссылку на сертификат (новый формат ответа)
      const certRes = await fetch(`${API_BASE}/api/generate-certificate/${userKursId}/`, {
        headers: { ...authHeader() }
      });
      if (!certRes.ok) throw new Error(`Certificate link failed: ${certRes.status}`);

      const data = await certRes.json();
      // ожидаем { message: "Sertifikat yaratildi", sertifikat_url: "<url|/relative/path>" }
      let url = data?.sertifikat_url;

      if (!url || typeof url !== 'string') {
        throw new Error("Certificate URL (sertifikat_url) not found in response");
      }

      // если пришёл относительный путь — склеим с API_BASE
      if (!/^https?:\/\//i.test(url)) {
        url = `${API_BASE}${url.startsWith('/') ? '' : '/'}${url}`;
      }

      // Открываем сертификат (новая вкладка)
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error(err.message);
      alert((t('error') || 'Xatolik') + ': ' + err.message);
    }
  };

  // 6) Кнопки после завершения
  const renderFinishLinks = () => {
    const percent = questions.length ? Math.round((score / questions.length) * 100) : 0;
    const passed = score >= minScore;

    return (
      <div className='test__over__links'>
        {/* назад к списку тем курса */}
        <Link to={`/Course/${kursId}`} className='register__btn btn__passive'>
          {t('backLesson')}
        </Link>

        {/* если прошёл — показать обе кнопки */}
        {passed ? (
          <>
            <button
              className='register__btn'
              onClick={() => {
                setFinished(false);
                setCurrent(0);
                setSelected(null);
                setScore(0);
                answersRef.current = [];
                setTotalTime(Math.max(0, (testCount || questions.length) * 60));
              }}
            >
              {t('retry') || 'Попробовать снова'}
            </button>

            <button className='register__btn' onClick={handleGetCertificate}>
              {t('getResult') || 'Получить результат'}*
            </button>
            <p className='testComment'>*{t('getResult')} {t('testComment')}</p>
          </>
        ) : (
          // если не прошёл — только повтор
          <button
            className='register__btn'
            onClick={() => {
              setFinished(false);
              setCurrent(0);
              setSelected(null);
              setScore(0);
              answersRef.current = [];
              setTotalTime(Math.max(0, (testCount || questions.length) * 60));
            }}
          >
            {t('retry') || 'Попробовать снова'}
          </button>
        )}
      </div>
    );
  };

  // Вспомогательные вычисления
  const minutes = Math.floor(totalTime / 60);
  const seconds = totalTime % 60;
  const formattedTime = `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  const progress = questions.length ? (current / questions.length) * 100 : 0;

  // Рендер
  if (guardLoading) return <p className='container'>Yuklanmoqda...</p>;
  if (error) {
    const locked = error.includes('Final test is locked');
    return (
      <div className="container">
        <div className="quiz-container quiz-container-result">
          <h2 style={{ color: locked ? '#d9534f' : 'inherit' }}>
            {locked ? t('accessDenied') || 'Access denied' : 'Xatolik'}
          </h2>
          <p>
            {locked
              ? (t('finishAllTopicsToUnlock') || 'Please finish all topics before taking the final test.')
              : error}
          </p>
          <div className='test__over__links'>
            <Link to={`/Course/${kursId}`} className='register__btn btn__passive'>
              {t('backLesson')}
            </Link>
          </div>
        </div>
      </div>
    );
  }
  if (loading) return <p className='container'>Yuklanmoqda...</p>;
  if (questions.length === 0) return <p className='container'>Savollar topilmadi.</p>;

  if (finished) {
    const percent = Math.round((score / questions.length) * 100);
    return (
      <div className="container">
        <div className="quiz-container quiz-container-result">
          <h2>{t('result')} {percent}%</h2>
          <p>{t('answer1')} {score} {t('answer2')} {questions.length} {t('answer3')}</p>
          {totalTime === 0 && <p style={{ color: 'red' }}>{t('timeOver')}</p>}
          {renderFinishLinks()}
        </div>
      </div>
    );
  }

  const currentQuestion = questions[current];

  return (
    <div className="container">
      <div className="quiz-container">
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${progress}%` }} />
        </div>

        <div className="timer">{formattedTime} {t('left')}</div>

        <h2 dangerouslySetInnerHTML={{ __html: currentQuestion.question }} />
        <div className="answers">
          {currentQuestion.answers.map((answer, idx) => (
            <button
              key={idx}
              className={`answer-button ${selected === idx ? 'selected' : ''}`}
              onClick={() => setSelected(idx)}
              dangerouslySetInnerHTML={{ __html: answer.text }}
            />
          ))}
        </div>

        <button className="next-button" onClick={handleNext} disabled={selected === null}>
          {current === questions.length - 1 ? t('finish') : t('next')}
        </button>
      </div>
    </div>
  );
};
